// Reglas de reservas de zonas sociales, sin acceso a Firebase.
// Es copia de la lógica de la app y debe dar exactamente los mismos resultados:
//   coordiph/lib/widgets/modulo_reservas/crear_reserva.dart
//   coordiph/lib/function/firebase.dart (validarAforo, _ocupaHorario, ajustarContadoresReserva)

export const MAX_INVITADOS = 15;

export const HORAS_INTERMEDIAS = Array.from({ length: 48 }, (_, i) =>
  `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);

export const HORAS_COMPLETAS = Array.from({ length: 24 }, (_, i) =>
  `${String(i).padStart(2, "0")}:00`);

export const DIAS_SEMANA = ["", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// Campos de ReservaModel (coordiph/lib/models/modelo_reservas.dart).
const CAMPOS_INVITADOS = Array.from({ length: MAX_INVITADOS }, (_, i) => [
  `numeroDocumentoInvitado${i + 1}`, `tipoDocumentoInvitado${i + 1}`, `nombreInvitado${i + 1}`,
]).flat();
export const CAMPOS_RESERVA = [
  "usuario", "motivoRechazo", "correo", "idAprobado", "urlPago", "id", "solicitarDatosVisitantes",
  "zonaId", "requiereAforo", "autorizado", "cantidadSedes", "zona", "estado", "fecha", "reservaUnica",
  "cantidadHorasReserva", "hora", "horaInicio", "horaFinal", "observacion", "casa", "titular",
  "telefonoTitular", "cantidadInvitados", ...CAMPOS_INVITADOS, "tituloReserva", "descripcionReserva",
  "sedeSeleccionada", "urlReserva", "fechaCreacionRegistro",
];

// Equivale a ReservaModel.fromJson(...).toJson(): conserva solo los campos del modelo
// y quita los vacíos (null), igual que hace Firebase al guardar desde la app.
export const reservaAJson = (r) => {
  const salida = {};
  for (const k of CAMPOS_RESERVA) {
    if (r?.[k] !== undefined && r?.[k] !== null) salida[k] = r[k];
  }
  return salida;
};

// Correo como clave: la app quita "@" y "." (sin pasar a minúsculas) en las copias de la reserva…
export const correoClave = (correo) => String(correo ?? "").replace(/@/g, "").replace(/\./g, "");
// …y en los contadores de cupo además lo pasa a minúsculas.
export const correoClaveConteo = (correo) => correoClave(String(correo ?? "").toLowerCase());

// DateTime.now().toString() de Dart: "2026-10-14 09:05:03.120".
export const fechaHoraDart = (d = new Date()) => {
  const p = (n, l = 2) => String(n).padStart(l, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
};
export const fechaISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// DateTime.tryParse("yyyy-MM-dd") de Dart: medianoche local, o null si no es fecha.
export const parsearFecha = (f) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(f ?? ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
// DateTime.weekday de Dart: lunes = 1 … domingo = 7.
export const diaSemanaDart = (d) => (d.getDay() === 0 ? 7 : d.getDay());
const sumarDias = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// double.toString() de Dart: 2 -> "2.0", 1.5 -> "1.5".
export const duracionTexto = (n) => (Number.isInteger(n) ? n.toFixed(1) : String(n));

// crear_reserva.dart → diferencia(). Devuelve null si alguna hora no es válida
// (la app muestra "No existen reservas disponibles para esta fecha." y cierra).
export const diferencia = (inicio, fin) => {
  const aMin = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(t ?? ""));
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = aMin(inicio), b = aMin(fin);
  if (a === null || b === null) return null;
  const minutos = b - a;
  const horas = Math.trunc(minutos / 60);                     // Duration.inHours
  const resto = ((minutos % 60) + 60) % 60;                   // % de Dart (nunca negativo)
  return horas + (resto === 30 ? 50 : 0) / 100;
};

// crear_reserva.dart → _parseHora() / _formatearHora()
export const parseHora = (hora) => {
  const h = parseInt(String(hora).substring(0, 2), 10);
  const m = String(hora).substring(3) === "30" ? 50 : 0;
  return h + m / 100;
};
export const formatearHora = (hora) => {
  const horas = Math.trunc(hora);
  const minutos = (hora - horas) * 100 === 50 ? 30 : 0;
  return `${String(horas).padStart(2, "0")}:${String(minutos).padStart(2, "0")}`;
};

// String de un double en Dart: siempre lleva parte decimal ("14.0", "14.5").
const decimalesDart = (n) => {
  const t = duracionTexto(n).split(".");
  return t.length > 1 ? t[1] : "0";
};

// crear_reserva.dart → _validarConflictoHorario()
export const validarConflictoHorario = (otra, startHour, endHour, proximaReservaDescanso) => {
  const startHourConsulta = parseHora(otra.horaInicio);
  const endHourConsulta = parseHora(otra.horaFinal);

  const solapan = (startHour >= startHourConsulta && startHour < endHourConsulta) ||
    (endHour > startHourConsulta && endHour <= endHourConsulta) ||
    (startHour <= startHourConsulta && endHour >= endHourConsulta);

  if (solapan) return "Ya existe una reserva para este horario, por favor seleccione otro horario.";

  if (proximaReservaDescanso !== true) return null;

  let endConDescanso = endHourConsulta + 1.0;
  if (decimalesDart(endHourConsulta).startsWith("5")) endConDescanso = Math.trunc(endHourConsulta) + 1.5;
  if (startHour >= endHourConsulta && startHour < endConDescanso) {
    return `Debe respetar un tiempo de descanso de 1 hora después de la última reserva. La próxima reserva disponible es a partir de las ${formatearHora(endConDescanso)}`;
  }

  let startMenosDescanso = startHourConsulta - 1.0;
  if (decimalesDart(startHourConsulta).startsWith("5")) startMenosDescanso = Math.trunc(startHourConsulta) - 0.5;
  if (endHour > startMenosDescanso && endHour <= startHourConsulta) {
    return `Debe respetar un tiempo de descanso de 1 hora antes de la siguiente reserva. Su reserva debe finalizar como máximo a las ${formatearHora(startMenosDescanso)}`;
  }
  return null;
};

// crear_reserva.dart → _motivoConflicto(): filtra el listado y devuelve el primer conflicto.
// `listado` son las reservas de reservas/aprobados (+ pendientes si contarPendientes).
// Con `soloAnteriores` solo cuentan las creadas antes que `propia` (verificación tras guardar).
export const motivoConflicto = ({ listado, fecha, zonaId, sede, startHour, endHour, proximaReservaDescanso, soloAnteriores = false, propia = null }) => {
  for (const r of listado) {
    const mismoSlot = r.fecha === fecha && r.zonaId === zonaId && r.sedeSeleccionada === sede && r.estado !== "Anulado";
    if (!mismoSlot) continue;
    if (soloAnteriores) {
      if (r.id === propia.id) continue;
      const suya = r.fechaCreacionRegistro ?? "";
      const mia = propia.fechaCreacionRegistro ?? "";
      const esAnterior = suya !== mia ? suya < mia : (r.id ?? "") < (propia.id ?? "");
      if (!esAnterior) continue;
    }
    const conflicto = validarConflictoHorario(r, startHour, endHour, proximaReservaDescanso);
    if (conflicto !== null) return conflicto;
  }
  return null;
};

// crear_reserva.dart → _motivoDia()
const DIAS_CAMPOS = { 1: "reservaLunes", 2: "reservaMartes", 3: "reservaMiercoles", 4: "reservaJueves", 5: "reservaViernes", 6: "reservaSabado", 7: "reservaDomingo" };
export const motivoDia = (fecha, permisosZona) => {
  const ahora = parsearFecha(fecha);
  if (!ahora) return null;
  const dia = diaSemanaDart(ahora);
  const autorizado = permisosZona?.[DIAS_CAMPOS[dia]] ?? true;
  if (autorizado) return null;
  return `El día ${DIAS_SEMANA[dia]} no está habilitado para reservar en esta zona.`;
};

// crear_reserva.dart → _motivoFechaLimite(). `ahora` incluye la hora actual, igual que DateTime.now().
export const motivoFechaLimite = (fecha, permisosZona, ahora = new Date()) => {
  const proxima = permisosZona?.proximaReserva;
  if (!permisosZona || proxima === null || proxima === undefined || proxima <= 0) return null;
  const DIA = 24 * 60 * 60 * 1000;
  const seleccionada = parsearFecha(fecha);
  const limite = new Date(ahora.getTime() + proxima * DIA);
  if (seleccionada.getTime() > ahora.getTime() - DIA && seleccionada.getTime() < limite.getTime()) return null;
  return `Solo se puede reservar desde hoy (${fechaISO(ahora)}) hasta el ${fechaISO(limite)}.`;
};

// crear_reserva.dart → _urlsConteo() y firebase.dart → ajustarContadoresReserva()
export const urlsConteo = (correo, codigo, zonaId, fecha) => {
  const ahora = parsearFecha(fecha);
  if (!ahora) return null;
  const wd = diaSemanaDart(ahora);
  const semana = `${fechaISO(sumarDias(ahora, -(wd - 1)))}_${fechaISO(sumarDias(ahora, 7 - wd))}`;
  const mes = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, "0")}`;
  const base = `usuarios/${correoClaveConteo(correo)}/conteo_reservas/${codigo}/${zonaId}`;
  return { dia: `${base}/dia/${fecha}`, semana: `${base}/semana/${semana}`, mes: `${base}/mes/${mes}` };
};

// firebase.dart → _ocupaHorario(): una reserva ocupa el horario si no está anulada y es de
// la misma sede (o no tiene sede definida / aplica a todas).
const SIN_SEDE = new Set(["null", "", "0", "1", "todo"]);
export const ocupaHorario = (r, sede) => {
  if (r.estado === "Anulado") return false;
  if (sede === null || sede === undefined || sede === "" || SIN_SEDE.has(sede)) return true;
  const s = r.sedeSeleccionada;
  return s === null || s === undefined || SIN_SEDE.has(s) || s === sede;
};

// firebase.dart → validarAforo() + validarAforo2(): con las reservas de aprobados/{zona}/{fecha}
// devuelve las horas que se pueden elegir y la ocupación por hora.
export const calcularAforo = (horas, ocupadas, sede) => {
  const aforo = Object.fromEntries(horas.map(h => [h, 0]));
  for (const r of ocupadas) {
    if (!r || typeof r !== "object" || !ocupaHorario(r, sede)) continue;
    const ini = horas.indexOf(r.horaInicio);
    let fin = horas.indexOf(r.horaFinal);
    if (ini === -1) continue;
    if (fin < ini) fin = ini;
    const personas = (r.cantidadInvitados ?? 0) < 1 ? 1 : r.cantidadInvitados;
    for (let i = ini; i <= fin; i++) aforo[horas[i]] += personas;
  }
  const libres = [];
  for (let i = 0; i < horas.length; i++) {
    if (aforo[horas[i]] === 0) libres.push(horas[i]);
    else if (i + 1 < horas.length && aforo[horas[i + 1]] === 0) libres.push(horas[i]);
    if (i > 0 && aforo[horas[i]] > 0 && aforo[horas[i - 1]] === 0) libres.push(horas[i]);
  }
  return { libres, aforo };
};

// crear_reserva.dart → generarDireccion()
export const generarDireccion = (tipoCasa, sector, casa) => {
  if (tipoCasa === "APARTAMENTO") return `TORRE ${sector} APARTAMENTO ${casa}`;
  if (sector) return `MANZANA ${sector} CASA ${casa}`;
  return `CASA ${casa}`;
};

// firebase.dart → cargarListaUsuarios(): viviendas que el personal puede elegir al reservar.
const COORDINAR = [
  "ANALISTA COMERCIAL", "ANALISTA DE CARTERA", "ANALISTA DE NOMINA", "COORDINADOR DE DESARROLLO",
  "AUXILIAR DE SISTEMAS", "ASESOR TECNOLOGÍA", "AUXILIAR ADMINISTRATIVA", "AUXILIAR DE CONSULTORÍA",
  "AUXILIAR DE CONTRATACIÓN", "AUXILIAR DE GESTIÓN HUMANA", "AUXILIAR DE SERVICIOS", "AUXILIAR SIG",
  "COORDINADOR CONTABLE", "COORDINADOR GH", "COORDINADOR SGSST", "COORDINADORA ADMINISTRATIVA",
  "COORDINADORA GH", "DIRECTOR DE SERVICIOS", "DIRECTOR DE TECNOLOGÍA", "DIRECTORA COMERCIAL",
  "DIRECTORA DE SERVICIOS", "DIRECTORA FINANCIERA Y ADMIN", "DIRECTORA GESTIÓN HUMANA", "DIRECTORA SIG",
  "EJECUTIVO CUENTA 1", "EJECUTIVO CUENTA 2", "EJECUTIVO CUENTA 3", "EJECUTIVO CUENTA 4",
  "EJECUTIVO CUENTA 5", "GERENCIA", "JEFE DE SEGURIDAD", "KEY ACCOUNT MANAGER",
  "OPERADOR DE MEDIOS TECNOLÓGICOS", "PRACTICANTE ADMIN", "PROGRAMADOR", "SUPERVISOR TÉCNICO",
  "TÉCNICO DE MEDIOS TECNOLÓGICOS",
];
export const listaViviendasPersonal = (codigo) => {
  const lista = [];
  for (const d of ["ADICIONAR MANUALMENTE", "ADMINISTRACIÓN", "COPROPIEDAD", "PORTERIA"]) lista.unshift(d);
  if (codigo === "900362633") for (const d of COORDINAR) lista.splice(1, 0, d);
  return lista;
};
